import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { realtimeAPI, notificationAPI } from '../lib/api';
import CalendarSubscriptionModal from './CalendarSubscriptionModal';
import {
  LayoutDashboard,
  Users,
  Building2,
  ClipboardList,
  Calendar,
  FileText,
  Receipt,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  TrendingUp,
  CheckSquare,
  Sparkles,
  Layers,
  Send,
  Shield,
  BarChart3,
  Briefcase,
  CalendarDays,
  CalendarOff,
  BookOpen,
  UserCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { rbacAPI } from '../lib/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_accred-portal/artifacts/ux88ej0x_steward.png';

// Icon map for dynamic sidebar building
const ICON_MAP = {
  '/dashboard': LayoutDashboard,
  '/analytics': BarChart3,
  '/leads': TrendingUp,
  '/clients': Building2,
  '/phases': ClipboardList,
  '/phase-master': Layers,
  '/accreditation-standards': BookOpen,
  '/access-control': Shield,
  '/tasks': CheckSquare,
  '/roster': CalendarDays,
  '/leaves': CalendarOff,
  '/attendance': UserCheck,
  '/visits': Calendar,
  '/documents': FileText,
  '/invoices': Receipt,
  '/tickets': MessageSquare,
  '/communications': Send,
  '/audit-trail': Shield,
  '/reports': FileText,
  '/ai-tools': Sparkles,
  '/users': Users,
  '/my-portal': Briefcase,
  '/my-progress': ClipboardList,
  '/staff-dashboard': LayoutDashboard,
  '/client-dashboard': LayoutDashboard,
};

const AdminSidebarItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  { icon: TrendingUp, label: 'Leads', path: '/leads' },
  { icon: Building2, label: 'Clients', path: '/clients' },
  { icon: ClipboardList, label: 'Phases', path: '/phases' },
  { icon: Layers, label: 'Phase Master', path: '/phase-master' },
  { icon: BookOpen, label: 'Standards', path: '/accreditation-standards' },
  { icon: Shield, label: 'Access Control', path: '/access-control' },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
  { icon: CalendarDays, label: 'Roster', path: '/roster' },
  { icon: CalendarOff, label: 'Leave Management', path: '/leaves' },
  { icon: UserCheck, label: 'Attendance', path: '/attendance' },
  { icon: Calendar, label: 'Visits', path: '/visits' },
  { icon: MessageSquare, label: 'Tickets', path: '/tickets' },
  { icon: Send, label: 'Communications', path: '/communications' },
  { icon: Shield, label: 'Audit Trail', path: '/audit-trail' },
  { icon: FileText, label: 'Reports', path: '/reports' },
  { icon: Users, label: 'Users', path: '/users' },
];

// Default fallbacks (used before RBAC loads)
const StaffSidebarDefaults = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/staff-dashboard' },
  { icon: Building2, label: 'My Clients', path: '/clients' },
  { icon: ClipboardList, label: 'Phases', path: '/phases' },
  { icon: CheckSquare, label: 'My Tasks', path: '/tasks' },
  { icon: CalendarDays, label: 'My Roster', path: '/roster' },
  { icon: CalendarOff, label: 'My Leaves', path: '/leaves' },
  { icon: Calendar, label: 'My Visits', path: '/visits' },
  { icon: MessageSquare, label: 'Tickets', path: '/tickets' },
];

const ClientSidebarDefaults = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/client-dashboard' },
  { icon: Briefcase, label: 'My Portal', path: '/my-portal' },
  { icon: ClipboardList, label: 'My Progress', path: '/my-progress' },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
  { icon: Calendar, label: 'Visits', path: '/visits' },
  { icon: MessageSquare, label: 'Support', path: '/tickets' },
];

// RBAC path-to-key mapping
const PATH_TO_KEY = {
  '/dashboard': 'dashboard', '/staff-dashboard': 'dashboard', '/client-dashboard': 'dashboard',
  '/analytics': 'analytics', '/leads': 'leads', '/clients': 'clients',
  '/phases': 'phases', '/phase-master': 'phase_master',
  '/accreditation-standards': 'standards', '/access-control': 'rbac_settings',
  '/tasks': 'tasks', '/roster': 'roster', '/leaves': 'leaves', '/attendance': 'attendance',
  '/visits': 'visits', '/documents': 'documents', '/invoices': 'invoices',
  '/tickets': 'tickets', '/communications': 'communications',
  '/audit-trail': 'audit_trail', '/ai-tools': 'ai_tools',
  '/reports': 'reports',
  '/users': 'users', '/my-portal': 'my_portal', '/my-progress': 'my_progress',
};

// Map sidebar paths -> sidebarCounts key (used to render red badges)
const PATH_TO_COUNT_KEY = {
  '/tasks': 'tasks',
  '/visits': 'visits',
  '/leaves': 'leaves',
  '/tickets': 'tickets',
};

// Per-path icon pill colors — keeps the sidebar colorful without becoming noisy.
// {bg, text} are Tailwind classes applied to the icon container.
const PATH_TO_COLOR = {
  '/dashboard':       { bg: 'bg-indigo-500/20',  text: 'text-indigo-300' },
  '/staff-dashboard': { bg: 'bg-indigo-500/20',  text: 'text-indigo-300' },
  '/client-dashboard':{ bg: 'bg-indigo-500/20',  text: 'text-indigo-300' },
  '/analytics':       { bg: 'bg-violet-500/20',  text: 'text-violet-300' },
  '/leads':           { bg: 'bg-fuchsia-500/20', text: 'text-fuchsia-300' },
  '/clients':         { bg: 'bg-sky-500/20',     text: 'text-sky-300' },
  '/phases':          { bg: 'bg-cyan-500/20',    text: 'text-cyan-300' },
  '/phase-master':    { bg: 'bg-teal-500/20',    text: 'text-teal-300' },
  '/accreditation-standards': { bg: 'bg-emerald-500/20', text: 'text-emerald-300' },
  '/standards':       { bg: 'bg-emerald-500/20', text: 'text-emerald-300' },
  '/tasks':           { bg: 'bg-amber-500/20',   text: 'text-amber-300' },
  '/roster':          { bg: 'bg-orange-500/20',  text: 'text-orange-300' },
  '/leaves':          { bg: 'bg-purple-500/20',  text: 'text-purple-300' },
  '/attendance':      { bg: 'bg-rose-500/20',    text: 'text-rose-300' },
  '/visits':          { bg: 'bg-blue-500/20',    text: 'text-blue-300' },
  '/documents':       { bg: 'bg-lime-500/20',    text: 'text-lime-300' },
  '/invoices':        { bg: 'bg-emerald-500/20', text: 'text-emerald-300' },
  '/tickets':         { bg: 'bg-pink-500/20',    text: 'text-pink-300' },
  '/communications':  { bg: 'bg-yellow-500/20',  text: 'text-yellow-300' },
  '/audit':           { bg: 'bg-slate-500/20',   text: 'text-slate-300' },
  '/audit-trail':     { bg: 'bg-slate-500/20',   text: 'text-slate-300' },
  '/reports':         { bg: 'bg-indigo-500/20',  text: 'text-indigo-300' },
  '/ai-tools':        { bg: 'bg-fuchsia-500/20', text: 'text-fuchsia-300' },
  '/users':           { bg: 'bg-red-500/20',     text: 'text-red-300' },
  '/access-control':  { bg: 'bg-red-500/20',     text: 'text-red-300' },
  '/my-portal':       { bg: 'bg-sky-500/20',     text: 'text-sky-300' },
  '/my-progress':     { bg: 'bg-cyan-500/20',    text: 'text-cyan-300' },
};
const DEFAULT_COLOR = { bg: 'bg-slate-500/20', text: 'text-slate-300' };

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [allowedKeys, setAllowedKeys] = useState(null);
  const [sidebarCounts, setSidebarCounts] = useState({ tasks: 0, visits: 0, leaves: 0, tickets: 0 });
  const [showCalendarSubModal, setShowCalendarSubModal] = useState(false);
  const wsRef = useRef(null);

  // Refresh sidebar pending-action counts (red badges)
  const refreshSidebarCounts = useCallback(() => {
    notificationAPI.getSidebarCounts()
      .then(res => setSidebarCounts(res.data || { tasks: 0, visits: 0, leaves: 0, tickets: 0 }))
      .catch(() => {});
  }, []);

  // Fetch RBAC menu config
  useEffect(() => {
    rbacAPI.getMyMenu()
      .then(res => setAllowedKeys(res.data.menu_keys || []))
      .catch(() => setAllowedKeys(null));
  }, []);

  // Poll sidebar counts every 60s + on route change
  useEffect(() => {
    if (!user) return;
    refreshSidebarCounts();
    const t = setInterval(refreshSidebarCounts, 60000);
    return () => clearInterval(t);
  }, [user, refreshSidebarCounts]);

  useEffect(() => {
    if (user) refreshSidebarCounts();
  }, [location.pathname, user, refreshSidebarCounts]);

  const getSidebarItems = () => {
    const role = user?.role;
    // Super Admin always sees the full Admin sidebar (incl. Access Control)
    if (role === 'super_admin') return AdminSidebarItems;

    // Admin / Manager: start from the full Admin sidebar but
    //   (a) always hide /access-control (super-admin-only page)
    //   (b) filter by RBAC config if loaded
    if (role === 'admin' || role === 'manager') {
      const base = AdminSidebarItems.filter(i => i.path !== '/access-control');
      if (!allowedKeys) return base;
      return base.filter(item => {
        const key = PATH_TO_KEY[item.path];
        return key ? allowedKeys.includes(key) : true;
      });
    }

    // Get default items for the role
    const defaults = role === 'staff' ? StaffSidebarDefaults : ClientSidebarDefaults;
    if (!allowedKeys) return defaults;

    // Filter by RBAC config
    return defaults.filter(item => {
      const key = PATH_TO_KEY[item.path];
      return key ? allowedKeys.includes(key) : true;
    });
  };

  // Load stored notifications and connect WebSocket
  useEffect(() => {
    if (!user) return;
    
    // Load existing notifications
    realtimeAPI.getNotifications().then(res => {
      setNotifications(res.data.filter(n => !n.is_read).slice(0, 10));
    }).catch(() => {});

    // Connect WebSocket
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    if (backendUrl && token) {
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      try {
        const ws = new WebSocket(`${wsUrl}/ws/notifications/${token}`);
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'notification') {
              setNotifications(prev => [msg.data, ...prev].slice(0, 10));
            }
          } catch (e) {}
        };
        ws.onclose = () => {};
        wsRef.current = ws;
      } catch (e) {}
    }

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [user]);

  const handleNotificationClick = async (n) => {
    // Mark read first, then navigate to its link if any
    try {
      await realtimeAPI.markRead(n.id);
    } catch (e) {}
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications([]);
      refreshSidebarCounts();
    } catch (e) {}
  };

  const notificationCount = notifications.length;

  const sidebarItems = getSidebarItems();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar-shell fixed top-0 left-0 z-50 h-full w-64 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b sidebar-divider">
            <Link to="/" className="flex items-center gap-2">
              <span className="sidebar-logo">
                <img src={LOGO_URL} alt="Steward Healthcare" className="h-8 w-auto" />
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white hover:bg-white/10"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const countKey = PATH_TO_COUNT_KEY[item.path];
                const count = countKey ? (sidebarCounts[countKey] || 0) : 0;
                const color = PATH_TO_COLOR[item.path] || DEFAULT_COLOR;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className={`sidebar-icon ${color.bg} ${color.text}`}>
                        <Icon />
                      </span>
                      <span className="flex-1 text-sm">{item.label}</span>
                      {count > 0 && (
                        <Badge
                          variant="destructive"
                          className="ml-auto h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] font-bold shadow-lg shadow-red-500/40"
                          data-testid={`sidebar-badge-${countKey}`}
                        >
                          {count > 99 ? '99+' : count}
                        </Badge>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          <div className="p-3 border-t sidebar-divider">
            <div className="sidebar-user-card flex items-center gap-3 px-3 py-2.5 rounded-xl">
              <Avatar className="h-9 w-9 ring-2 ring-white/20">
                <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-pink-500 text-white text-sm font-bold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-white">{user?.name}</p>
                <p className="text-[11px] text-indigo-200/80 capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-header">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-heading font-semibold hidden sm:block">
                {sidebarItems.find((item) => item.path === location.pathname)?.label || 'Dashboard'}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {notificationCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                      >
                        {notificationCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="p-3 border-b flex items-center justify-between">
                    <h3 className="font-semibold">Notifications</h3>
                    <div className="flex items-center gap-2">
                      {notificationCount > 0 && (
                        <Badge variant="outline" className="text-xs">{notificationCount} new</Badge>
                      )}
                      {notificationCount > 0 && (
                        <button
                          type="button"
                          onClick={handleMarkAllRead}
                          className="text-xs text-primary hover:underline font-medium"
                          data-testid="mark-all-read-btn"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-6">
                        No new notifications
                      </p>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          role="button"
                          tabIndex={0}
                          className="p-3 hover:bg-slate-50 border-b last:border-0 cursor-pointer transition-colors"
                          onClick={() => handleNotificationClick(n)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleNotificationClick(n); }}
                          data-testid={`notification-row-${n.id}`}
                        >
                          <p className="text-sm font-medium">{n.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                          {n.link && (
                            <p className="text-[10px] text-primary mt-1 font-medium">View →</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Settings */}
              <Button variant="ghost" size="icon" data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="p-2">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="profile-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowCalendarSubModal(true)} data-testid="calendar-sub-menu-item">
                    <Calendar className="w-4 h-4 mr-2" />
                    Calendar Subscription
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>

      {/* Calendar Subscription Modal */}
      <CalendarSubscriptionModal open={showCalendarSubModal} onClose={() => setShowCalendarSubModal(false)} />
    </div>
  );
};

export default AppLayout;
